import { useState, useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import FiltersPanel from "../components/log-monitor/FiltersPanel";
import LogsTable from "../components/log-monitor/LogsTable";
// Summary cards removed — totals now shown in the table's count line below.
// import SummaryCards from "../components/log-monitor/SummaryCards";
import useLogs from "../components/log-monitor/useLogs";
import { cleanMessage, distinctMessages, distinctTags } from "../components/log-monitor/formatters";

// Error Monitor reuses the Log Monitor stack (same /app/v1/beadmin/logs API and
// components) but always queries errors and pulls a larger batch (limit 750).
// Kept as its own page so it can diverge from Log Monitor later.
export default function ErrorMonitor() {
  const { data, loading, loadingMore, hasMore, error, load, loadMore } = useLogs();

  // Collapsible stats beside the heading (hidden by default).
  const [showStats, setShowStats] = useState(false);

  // Totals for the stats line — counted on the cleaned core message so they match
  // the table's grouping.
  const { totalErrors, uniqueMessages } = useMemo(() => {
    const logs = data?.logs || [];
    const messages = new Set(logs.map((l) => cleanMessage(l.message)).filter(Boolean));
    return { totalErrors: logs.length, uniqueMessages: messages.size };
  }, [data]);

  // Distinct message labels fed to the Include/Exclude boxes (deduped so no repeats).
  const messageOptions = useMemo(() => distinctMessages(data?.logs || []), [data]);

  // Distinct tags from the response, fed to the Tags box.
  const tagOptions = useMemo(() => distinctTags(data?.logs || []), [data]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box className="headingBtnWrap">
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="h1">Daily Error Monitor</Typography>
          <IconButton
            size="small"
            onClick={() => setShowStats((prev) => !prev)}
            aria-label={showStats ? "Hide error stats" : "Show error stats"}
            aria-expanded={showStats}
          >
            {showStats ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </Box>
        <Collapse in={showStats} unmountOnExit>
          <Typography
            sx={{ mt: 0.5, fontSize: "0.9rem", fontWeight: 700, color: "text.secondary" }}
          >
            Total unique messages: {uniqueMessages} (Total errors: {totalErrors})
          </Typography>
        </Collapse>
      </Box>

      <Box sx={{ mt: "20px" }}>
        <FiltersPanel
          onFetch={load}
          loading={loading}
          limit={750}
          lockedEvent="error"
          lockedService="all"
          includeOptions={messageOptions}
          excludeOptions={messageOptions}
          tagOptions={tagOptions}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {data && (
          <Box>
            {/* Summary cards removed — the count line under the table
                ("N unique messages (M errors total)") reports the totals. */}
            <LogsTable
              logs={data.logs || []}
              hasMore={hasMore}
              loadingMore={loadingMore}
              onLoadMore={loadMore}
              groupByMessage
            />
          </Box>
        )}

        {!data && !loading && !error && (
          <Typography
            color="text.secondary"
            textAlign="center"
            sx={{ mt: 6, fontSize: "0.85rem" }}
          >
            Set filters and click Fetch to load errors.
          </Typography>
        )}
      </Box>
    </LocalizationProvider>
  );
}
